"use client";
import debounce from "lodash/debounce";
import { useCallback, useState, useEffect, useMemo } from "react";

import RangeSlider from "@stadium/ui/src/components/RangeSlider";

import styles from "./main.module.scss";

const RangeInput = ({ item, onChange, value, showRangeInputs = true }) => {
  const rangeOptions = item.options;
  const min = rangeOptions.min ?? 0;
  const max = rangeOptions.max ?? 1000;
  const initialMin = value?.min ?? min;
  const initialMax = value?.max ?? max;
  const prefix = rangeOptions.prefix || rangeOptions.suffix;

  const [localRangeState, setLocalRangeState] = useState([initialMin, initialMax]);
  const histogramData = rangeOptions.histogramData;
  const step = rangeOptions.step || 1;

  useEffect(() => {
    // If Redux has values, sync local state.
    // If Redux is empty (cleared), reset to default min/max.
    setLocalRangeState([value?.min ?? min, value?.max ?? max]);
  }, [value?.min, value?.max, min, max]);

  const handleRangeChange = useCallback(
    (_filterId: string, value: number[]) => {
      const [low, high] = value;
      onChange({ min: low, max: high });
    },
    [onChange],
  );

  const debouncedHandleRangeChange = useMemo(
    () =>
      debounce((filterId: string, value: number[]) => {
        handleRangeChange(filterId, value);
      }, 500),
    [handleRangeChange],
  );

  useEffect(() => {
    return () => {
      debouncedHandleRangeChange.cancel();
    };
  }, [debouncedHandleRangeChange]);

  return (
    <div className={styles.rangeSection}>
      <RangeSlider
        key={`${item.id}:${min}:${max}`}
        min={min}
        max={max}
        value={localRangeState}
        onChange={(val) => {
          setLocalRangeState(val);
        }}
        onChangeComplete={(val) => debouncedHandleRangeChange(item.id, val)}
        onInputChange={(val, type) => {
          debouncedHandleRangeChange(
            item.id,
            type === "minValue" ? [val, localRangeState[1]] : [localRangeState[0], val],
          );
        }}
        step={step}
        className={styles.rangeSlider}
        histogramData={histogramData}
        prefix={prefix}
        minTextInputLabel="Minimum"
        maxTextInputLabel="Maximum"
        showMinRangeInput={showRangeInputs}
        showMaxRangeInput={showRangeInputs}
        styles={styles}
      />
      {!showRangeInputs && (
        <div className={styles.rangeValues}>
          <span>
            {prefix}
            {localRangeState[0]}
          </span>
          <span>
            {prefix}
            {localRangeState[1]}
          </span>
        </div>
      )}
    </div>
  );
};

export default RangeInput;
